/**
 * Session quality plugin: return frequency, focus, and idle metrics.
 */
import type { PluginContext } from "../lib/types";
import { now, safeJsonParse } from "../lib/utils";

import { registerPlugin } from "./register";

export function createSessionQualityPlugin(ctx: PluginContext) {
  // Legacy timestamps cannot prove which session or browser visited. Keep the
  // versioned record separate so an old open tab cannot overwrite this state.
  const lastVisitKey = `prospix_last_visit_v2_${ctx.siteKey || "default"}`;
  const idleThresholdMs = 30000;

  let startedAt = now();
  let activeMs = 0;
  let lastActivityAt = startedAt;
  let lastTickAt = startedAt;
  let idleCount = 0;
  let focusSwitches = 0;
  let visible = document.visibilityState === "visible";
  let reportedProgress = "";
  let unsubscribeTrack: (() => void) | undefined;

  const tick = () => {
    const current = now();
    if (visible) {
      activeMs += Math.max(0, current - lastTickAt);
    }
    lastTickAt = current;
  };

  const markActivity = () => {
    const current = now();
    if (current - lastActivityAt >= idleThresholdMs) {
      idleCount += 1;
    }
    lastActivityAt = current;
  };

  const updateVisibility = (next: boolean) => {
    if (next === visible) return;
    tick();
    focusSwitches += 1;
    visible = next;
    if (visible) {
      markActivity();
    }
  };

  const emitReturnVisit = () => {
    if (!ctx.canTrackCategory("session_quality")) return;
    const observation = ctx.lastPageView;
    if (!observation) return;
    const raw = ctx.storage.get(lastVisitKey);
    type Visit = {
      version: 1;
      anonId: string;
      sessionId: string;
      seenAt: number;
    };
    const candidate = safeJsonParse<Partial<Visit> | null>(raw ?? "", null);
    const previous =
      candidate?.version === 1 &&
      typeof candidate.anonId === "string" &&
      typeof candidate.sessionId === "string" &&
      typeof candidate.seenAt === "number" &&
      Number.isFinite(candidate.seenAt) &&
      candidate.seenAt > 0
        ? (candidate as Visit)
        : undefined;
    if (previous && observation.occurredAt < previous.seenAt) return;
    const current: Visit = {
      version: 1,
      anonId: observation.anonId,
      sessionId: observation.sessionId,
      seenAt: observation.occurredAt,
    };
    const serialized = JSON.stringify(current);
    ctx.storage.set(lastVisitKey, serialized);
    if (
      !previous ||
      previous.anonId !== current.anonId ||
      previous.sessionId === current.sessionId ||
      ctx.storage.get(lastVisitKey) !== serialized
    )
      return;

    const deltaMs = current.seenAt - previous.seenAt;
    const days = Math.floor(deltaMs / (24 * 60 * 60 * 1000));
    ctx.track("return_visit", {
      since_last_ms: deltaMs,
      since_last_days: days,
    });
  };

  const emitSessionQuality = () => {
    if (!ctx.canTrackCategory("session_quality")) return;
    tick();
    const progress = `${activeMs}:${idleCount}:${focusSwitches}`;
    if (activeMs <= 0 || progress === reportedProgress) return;
    const totalMs = Math.max(0, now() - startedAt);
    ctx.track("session_quality", {
      total_ms: totalMs,
      active_ms: Math.max(0, Math.round(activeMs)),
      idle_count: idleCount,
      focus_switches: focusSwitches,
      visibility_state: document.visibilityState,
    });
    reportedProgress = progress;
  };

  const onPageHide = () => {
    updateVisibility(false);
    emitSessionQuality();
  };
  const onVisibility = () => {
    updateVisibility(document.visibilityState === "visible");
    if (!visible) emitSessionQuality();
  };
  const onPageShow = () =>
    updateVisibility(document.visibilityState === "visible");

  return {
    onInit() {
      startedAt = now();
      lastActivityAt = startedAt;
      lastTickAt = startedAt;
      visible = document.visibilityState === "visible";
      unsubscribeTrack = ctx.on("track", (payload) => {
        if (
          payload &&
          typeof payload === "object" &&
          "type" in payload &&
          payload.type === "page_view"
        )
          emitReturnVisit();
      });
      emitReturnVisit();

      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pageshow", onPageShow);
      window.addEventListener("mousemove", markActivity, { passive: true });
      window.addEventListener("keydown", markActivity, { passive: true });
      window.addEventListener("touchstart", markActivity, { passive: true });
      window.addEventListener("scroll", markActivity, { passive: true });
    },
    onPageHide,
    onUnload() {
      onPageHide();
      unsubscribeTrack?.();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("mousemove", markActivity);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("touchstart", markActivity);
      window.removeEventListener("scroll", markActivity);
    },
  };
}

registerPlugin("session_quality", createSessionQualityPlugin);
